import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Autocomplete,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { addBed, updateBed } from "../../services/BedService";
import { getAllWards } from "../../services/WardService";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import {
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
} from "../../utils/formModalStyles";

const bedTypeOptions = ["General", "ICU", "Private", "Deluxe"];
const occupancyOptions = ["Occupied", "Empty"];

const validationSchema = Yup.object({
  wardId: Yup.number().required("Ward is required"),
  bedNumber: Yup.string().required("Bed number is required"),
  isOccupied: Yup.string().required("Occupied status is required"),
  bedType: Yup.string().required("Bed type is required"),
  bedPrice: Yup.string().required("Bed price is required"),
});

const AddNewBed = ({ open, onClose, bedInfo, readOnly = false }) => {
  const [wardOptions, setWardOptions] = useState([]);

  // Fetch wards on modal open
  useEffect(() => {
    if (open) {
      getAllWards()
        .then((wards) => setWardOptions(wards))
        .catch((err) => {
          console.error("Failed to fetch wards:", err);
          setWardOptions([]);
        });
    }
  }, [open]);

  const formik = useFormik({
    initialValues: {
      wardId: bedInfo?.wardId || "",
      bedNumber: bedInfo?.bedNumber || "",
      isOccupied: bedInfo?.isOccupied ? "Occupied" : (bedInfo?.isOccupied === false ? "Empty" : ""),
      bedType: bedInfo?.bedType || "",
      bedPrice: bedInfo?.bedPrice || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          wardId: values.wardId,
          bedNumber: values.bedNumber,
          isOccupied: values.isOccupied === "Occupied", // boolean
          bedType: values.bedType,
          bedPrice: values.bedPrice,
        };

        if (bedInfo?.id) {
          await updateBed(bedInfo.id, payload);
          alert("Bed updated successfully");
        } else {
          await addBed(payload);
          alert("New ward bed added successfully");
        }
        handleCloseModal();
      } catch (error) {
        console.error("Failed to create ward bed:", error);
        alert("Failed to save bed.");
      }
    },
  });

  const handleCloseModal = () => {
    onClose();
    formik.resetForm();
  };

  const modalTitle = readOnly ? "View Bed" : bedInfo?.id ? "Edit Bed" : "Add New Bed";

  return (
    <BasicModal
      open={open}
      onClose={handleCloseModal}
      title={modalTitle}
    >
      <Box sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={wardOptions}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    wardOptions.find((w) => w.id === formik.values.wardId) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("wardId", newValue ? newValue.id : "");
                  }}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ward"
                      variant="outlined"
                      error={formik.touched.wardId && Boolean(formik.errors.wardId)}
                      helperText={formik.touched.wardId && formik.errors.wardId}
                      fullWidth
                      disabled={readOnly}
                      size="small"
                      margin="dense"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="bedNumber"
                  name="bedNumber"
                  label="Bed Number"
                  variant="outlined"
                  value={formik.values.bedNumber}
                  onChange={formik.handleChange}
                  error={formik.touched.bedNumber && Boolean(formik.errors.bedNumber)}
                  helperText={formik.touched.bedNumber && formik.errors.bedNumber}
                  disabled={readOnly}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="bedPrice"
                  name="bedPrice"
                  label="Bed Price"
                  variant="outlined"
                  value={formik.values.bedPrice}
                  onChange={formik.handleChange}
                  error={formik.touched.bedPrice && Boolean(formik.errors.bedPrice)}
                  helperText={formik.touched.bedPrice && formik.errors.bedPrice}
                  disabled={readOnly}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={bedTypeOptions}
                  value={formik.values.bedType || ""}
                  onChange={(event, newValue) => {
                    formik.setFieldValue("bedType", newValue || "");
                  }}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bed Type"
                      variant="outlined"
                      error={formik.touched.bedType && Boolean(formik.errors.bedType)}
                      helperText={formik.touched.bedType && formik.errors.bedType}
                      fullWidth
                      disabled={readOnly}
                      size="small"
                      margin="dense"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={occupancyOptions}
                  value={formik.values.isOccupied || ""}
                  onChange={(event, newValue) => {
                    formik.setFieldValue("isOccupied", newValue || "");
                  }}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Is Occupied"
                      variant="outlined"
                      error={formik.touched.isOccupied && Boolean(formik.errors.isOccupied)}
                      helperText={formik.touched.isOccupied && formik.errors.isOccupied}
                      fullWidth
                      disabled={readOnly}
                      size="small"
                      margin="dense"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                {readOnly ? (
                  <FormModalActions
                    onCancel={handleCloseModal}
                    cancelLabel="Close"
                    hideSubmit
                  />
                ) : (
                  <FormModalActions
                    onCancel={handleCloseModal}
                    submitType="submit"
                    submitDisabled={formik.isSubmitting}
                    loading={formik.isSubmitting}
                  />
                )}
              </Grid>
            </Grid>
          </form>
      </Box>
    </BasicModal>
  );
};

export default AddNewBed;